import React, { useMemo } from "react";
import { createAvatar } from "@dicebear/core";
import { lorelei } from "@dicebear/collection";
import axios from "axios";

function resolvePhotoUrl(url) {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const base = (axios.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${url}`;
}

const SKIN_MAP = {
  BRANCA:   ["ffdbac", "f2d3b1"],
  PARDA:    ["e0ac69", "c68642"],
  PRETA:    ["8d5524", "614335"],
  AMARELA:  ["f1c27d", "e0ac69"],
  INDÍGENA: ["c68642", "e0ac69"],
};

const HAIR_MASC = [
  "variant01", "variant04", "variant06", "variant08", "variant10",
  "variant12", "variant15", "variant18", "variant20",
];
const HAIR_FEM = [
  "variant23", "variant25", "variant28", "variant30", "variant32",
  "variant35", "variant38", "variant40", "variant43", "variant45",
];

function pick(arr, seed) {
  return arr[Math.abs(seed) % arr.length];
}

function hashStr(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return h;
}

function calcAge(dateStr) {
  if (!dateStr) return null;
  const birth = new Date(dateStr);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age;
}

function buildAvatarOptions(candidato) {
  const seed = hashStr(candidato.nome || candidato.nomeUrna || "avatar");
  const isMasc = (candidato.genero || candidato.ds_genero || "").toUpperCase().includes("MASC");
  const raca = (candidato.corRaca || candidato.raca || candidato.ds_cor_raca || "PARDA").toUpperCase();
  const escolaridade = (candidato.grauInstrucao || candidato.escolaridade || candidato.ds_grau_instrucao || "").toUpperCase();
  const age = calcAge(candidato.dataNascimento || candidato.dt_nascimento) || 45;

  const skinColor = SKIN_MAP[raca] || SKIN_MAP.PARDA;
  const hair = [pick(isMasc ? HAIR_MASC : HAIR_FEM, seed)];

  const hasSuperior = escolaridade.includes("SUPERIOR");
  const glasses = hasSuperior
    ? [pick(["variant01", "variant02", "variant03", "variant04"], seed + 1)]
    : [];
  const glassesProbability = hasSuperior ? 60 : 8;

  const beard = isMasc && age > 35 ? [pick(["variant01", "variant02"], seed + 2)] : [];
  const beardProbability = isMasc && age > 35 ? 45 : 0;

  const eyes = [pick([
    "variant01", "variant03", "variant05", "variant08", "variant10",
    "variant12", "variant15", "variant18", "variant20",
  ], seed + 3)];

  const mouth = [pick([
    "happy01", "happy02", "happy03", "happy04", "happy05",
    "happy06", "happy07", "happy08", "happy09", "happy10",
  ], seed + 4)];

  const eyebrows = [pick([
    "variant01", "variant02", "variant03", "variant04", "variant05",
    "variant06", "variant07", "variant08", "variant09",
  ], seed + 5)];

  const nose = [pick(["variant01", "variant02", "variant03", "variant04", "variant05", "variant06"], seed + 6)];

  return {
    seed: candidato.sqCandidato || candidato.nome || "avatar",
    skinColor,
    hair,
    glasses,
    glassesProbability,
    beard,
    beardProbability,
    eyes,
    mouth,
    eyebrows,
    nose,
    backgroundColor: ["transparent"],
  };
}

export default function CandidatoAvatar({
  candidato,
  foto,
  nome,
  size = 48,
  className = "",
  borderClass = "",
}) {
  const fotoUrl = resolvePhotoUrl(foto || candidato?.foto);
  const displayName = nome || candidato?.nomeUrna || candidato?.nome || "";

  const svgDataUri = useMemo(() => {
    if (!candidato) return null;
    const opts = buildAvatarOptions(candidato);
    const avatar = createAvatar(lorelei, opts);
    return `data:image/svg+xml;utf8,${encodeURIComponent(avatar.toString())}`;
  }, [candidato]);

  const [imgErr, setImgErr] = React.useState(false);
  const [retries, setRetries] = React.useState(0);

  React.useEffect(() => {
    setImgErr(false);
    setRetries(0);
  }, [fotoUrl]);

  React.useEffect(() => {
    if (imgErr && fotoUrl && retries < 2) {
      const timer = setTimeout(() => {
        setImgErr(false);
        setRetries(r => r + 1);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [imgErr, fotoUrl, retries]);

  const sizeStyle = { width: size, height: size, minWidth: size, minHeight: size };

  if (fotoUrl && !imgErr) {
    return (
      <img
        src={fotoUrl}
        alt={displayName}
        referrerPolicy="no-referrer"
        className={`rounded-full object-cover bg-[var(--border)] ${borderClass} ${className}`}
        style={sizeStyle}
        onError={() => setImgErr(true)}
      />
    );
  }

  if (svgDataUri) {
    return (
      <img
        src={svgDataUri}
        alt={displayName}
        className={`rounded-full bg-[var(--border)] ${borderClass} ${className}`}
        style={sizeStyle}
      />
    );
  }

  return (
    <div
      className={`rounded-full bg-[var(--border)] flex items-center justify-center text-xs font-bold text-[var(--accent)] ${borderClass} ${className}`}
      style={sizeStyle}
    >
      {displayName.split(" ").map(w => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
    </div>
  );
}
